import {
  Badge,
  Button,
  DataTable,
  FieldSwitch,
  FlexBox,
  IconButton,
  Page,
  Timestamp,
  Tooltip,
} from '@dixa/lib-shared-habla';
import type { Subscription, UpdateWebhookT } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  deleteSubscription,
  getSubscriptions,
  updateSubscription,
} from 'components/settings/api/webhooks';
import { ConfirmModal } from 'components/shared/confirmModal/components/confirmModal';

import { webhooksBasePath } from '../../webhooks.utils';

export function Subscriptions(): JSX.Element {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selected, setSelected] = useState<Subscription | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const [subscriptions, setSubscriptions] = useState<Subscription[] | []>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    getAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAll = useCallback(() => {
    setIsLoading(true);
    getSubscriptions()
      .then(({ data }) => {
        setSubscriptions(data.subscriptions);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        setIsError(true);
      });
  }, []);

  const handleRoute = (to: string) => navigate(`${webhooksBasePath}/${to}`);

  const handleRemove = (subscription: Subscription) => {
    setSelected(subscription);
    setShowConfirm(true);
    ampli.settingsIntegrationsWebhooksRemove();
  };

  const onRemove = useCallback(() => {
    setIsDeleting(true);

    if (selected) {
      const { id } = selected;

      deleteSubscription(id)
        .then(() => {
          ampli.settingsIntegrationsWebhooksRemoveConfirm();
          setIsDeleting(false);
          getAll();
        })
        .catch(() => setIsError(true));

      setShowConfirm(false);
    }
  }, [selected, getAll]);

  const onUpdate = useCallback(
    (payload: UpdateWebhookT) => {
      setIsUpdating(true);
      updateSubscription(payload)
        .then(() => {
          setIsUpdating(false);
          if (payload.is_enabled) {
            ampli.settingsIntegrationsWebhooksEnabledOn();
          } else {
            ampli.settingsIntegrationsWebhooksEnabledOff();
          }
          getAll();
        })
        .catch(() => setIsUpdating(false));
    },
    [getAll],
  );

  return (
    <>
      <Page.Section
        title="All webhooks"
        titleMeta={
          <>
            {subscriptions?.length > 0 && (
              <Badge size="small" variant="secondary">
                {subscriptions.length}
              </Badge>
            )}

            <Tooltip content="Help and documentation.">
              <IconButton
                icon="question"
                variant="tertiary"
                onClick={() => {
                  ampli.settingsIntegrationsWebhooksDocumentationLinkClicked();

                  window.open(
                    'https://docs.dixa.io/docs/webhooks-guide',
                    '_blank',
                    'noopener,noreferrer',
                  );
                }}
              />
            </Tooltip>
          </>
        }
        controls={
          <Button
            onClick={() => {
              ampli.settingsIntegrationsWebhooksAdd();
              handleRoute('create');
            }}
          >
            Add webhook
          </Button>
        }
      >
        <DataTable<Subscription, never>
          data={subscriptions}
          tableStyleProps={{
            stickyHeader: true,
          }}
          tableStatusProps={{
            loading: isLoading,
            loadingFailed: isError,
            onLoadingRetry: () => getAll(),
            emptyState:
              !subscriptions.length && !isLoading && !isError
                ? {
                    title: 'Nothing to show here',
                    description: 'No webhooks created yet',
                  }
                : undefined,
          }}
          columns={[
            {
              label: 'Name',
              selector: ({ name }) => name,
            },
            {
              label: 'Updated',
              selector: ({ updated_at }) => <Timestamp time={updated_at} />,
            },
            {
              label: 'Updated by',
              selector: ({ updated_by }) => updated_by.name || updated_by.id,
            },
            {
              label: 'Enabled',
              selector: ({ id, name, is_enabled }) => (
                <FieldSwitch
                  key="enabled"
                  disabled={isUpdating}
                  input={{
                    name,
                    value: name,
                    checked: is_enabled,
                    onChange: () =>
                      onUpdate({
                        id,
                        is_enabled: !is_enabled,
                      }),
                  }}
                />
              ),
            },
            {
              label: undefined,
              style: {
                width: 0,
              },
              selector: (subscription) => (
                <FlexBox flexDirection="row">
                  <Tooltip content="View">
                    <IconButton
                      variant="tertiary"
                      icon="eye"
                      onClick={() => {
                        ampli.settingsIntegrationsWebhooksView();
                        handleRoute(subscription.id);
                      }}
                    />
                  </Tooltip>
                  <Tooltip content="Edit">
                    <IconButton
                      variant="tertiary"
                      icon="pen"
                      onClick={() => {
                        ampli.settingsIntegrationsWebhooksEdit();
                        handleRoute(`edit/${subscription.id}`);
                      }}
                    />
                  </Tooltip>
                  <Tooltip content="Remove">
                    <IconButton
                      variant="tertiary"
                      icon="trash"
                      onClick={() => handleRemove(subscription)}
                    />
                  </Tooltip>
                </FlexBox>
              ),
            },
          ]}
        />
      </Page.Section>

      <ConfirmModal
        isShown={showConfirm}
        headline="Are you sure?"
        text={`Are you sure that you want to delete ${
          selected?.name || 'this webhook subscription'
        }? This means that events will no longer be sent to your specified destination.`}
        confirmText="Yes, delete it now"
        cancelText="Cancel"
        confirm={onRemove}
        confirmProps={{ isLoading: isDeleting }}
        cancel={() => {
          if (selected) {
            ampli.settingsIntegrationsWebhooksRemoveCancel();
          }
          setShowConfirm(false);
          setSelected(null);
        }}
      />
    </>
  );
}
